// Portfolio Data Firestore Management
// Fetches data dynamically from Firestore only (no localStorage)

import { 
  doc, 
  getDoc, 
  setDoc, 
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../config/firebase';

// Collection names for each section
const COLLECTIONS = {
  personal: 'personal',
  about: 'about',
  skills: 'skills',
  projects: 'projects',
  experience: 'experience',
  contact: 'contact',
  footer: 'footer'
};

const DOC_ID = 'data'; // Single document ID for each collection

const TRAVAUX_IA_APP_STORE_URL =
  'https://apps.apple.com/us/app/travaux-ia/id6779563040';

const patchProjectsSection = (projects) => {
  if (!projects?.items?.length) return projects;

  return {
    ...projects,
    items: projects.items.map((item) => {
      if (item.title !== 'Travaux IA') return item;

      return {
        ...item,
        links: (item.links || []).map((link) => {
          if (
            link.icon === 'fab fa-app-store-ios' ||
            /app store/i.test(link.text || '')
          ) {
            return {
              ...link,
              text: 'App Store',
              url: TRAVAUX_IA_APP_STORE_URL,
              type: 'primary',
            };
          }
          return link;
        }),
      };
    }),
  };
};

// Check if Firebase is configured
const isFirebaseConfigured = () => {
  try {
    return db && process.env.REACT_APP_FIREBASE_PROJECT_ID !== 'YOUR_PROJECT_ID' && process.env.REACT_APP_FIREBASE_PROJECT_ID;
  } catch (error) {
    return false;
  }
};

// Load a specific section from Firestore
const loadSection = async (sectionName) => {
  if (!isFirebaseConfigured()) {
    throw new Error('Firebase is not configured');
  }

  try {
    const sectionRef = doc(db, COLLECTIONS[sectionName], DOC_ID);
    const sectionSnap = await getDoc(sectionRef);
    
    if (sectionSnap.exists()) {
      const data = sectionSnap.data();
      // Remove Firestore metadata
      delete data.updatedAt;
      delete data.createdAt;
      return data;
    }
    return null;
  } catch (error) {
    console.error(`Error loading ${sectionName} from Firestore:`, error);
    throw error;
  }
};

// Save a specific section to Firestore
const saveSection = async (sectionName, sectionData) => {
  if (!isFirebaseConfigured()) {
    throw new Error('Firebase is not configured');
  }

  try {
    const sectionRef = doc(db, COLLECTIONS[sectionName], DOC_ID);
    const existingDoc = await getDoc(sectionRef);
    
    const updateData = {
      ...sectionData,
      updatedAt: serverTimestamp()
    };
    
    if (!existingDoc.exists()) {
      updateData.createdAt = serverTimestamp();
    }
    
    await setDoc(sectionRef, updateData, { merge: false });
    return { success: true };
  } catch (error) {
    console.error(`Error saving ${sectionName} to Firestore:`, error);
    throw error;
  }
};

// Load all portfolio data from Firestore
export const loadPortfolioFromFirestore = async () => {
  if (!isFirebaseConfigured()) {
    throw new Error('Firebase is not configured');
  }

  try {
    const portfolioData = {};
    
    // Load each section
    for (const sectionName of Object.keys(COLLECTIONS)) {
      const sectionData = await loadSection(sectionName);
      if (sectionData) {
        portfolioData[sectionName] = sectionData;
      }
    }
    
    // Check if we got any data
    if (Object.keys(portfolioData).length > 0) {
      if (portfolioData.projects) {
        portfolioData.projects = patchProjectsSection(portfolioData.projects);
      }
      return portfolioData;
    }
    
    return null;
  } catch (error) {
    console.error('Error loading portfolio from Firestore:', error);
    throw error;
  }
};

// Save portfolio data to Firestore (saves each section separately)
export const savePortfolioToFirestore = async (portfolioData) => {
  if (!isFirebaseConfigured()) {
    throw new Error('Firebase is not configured');
  }

  try {
    // Save each section separately
    const results = {};
    for (const sectionName of Object.keys(COLLECTIONS)) {
      if (portfolioData[sectionName]) {
        const result = await saveSection(sectionName, portfolioData[sectionName]);
        results[sectionName] = result;
      }
    }
    
    return { success: true, results };
  } catch (error) {
    console.error('Error saving portfolio to Firestore:', error);
    throw error;
  }
};

// Save a specific section
export const saveSectionToFirestore = async (sectionName, sectionData) => {
  if (!isFirebaseConfigured()) {
    throw new Error('Firebase is not configured');
  }

  try {
    return await saveSection(sectionName, sectionData);
  } catch (error) {
    console.error(`Error saving ${sectionName}:`, error);
    throw error;
  }
};
